<template>
  <v-sheet
    color="white"
    elevation="5"
    rounded
    class="mb-5 pt-3 pb-5 pl-3 pr-3"
    height="100%"
  >
    <v-overlay :value="loading">
      <v-progress-circular
        indeterminate
        color="primary"
        :size="70"
        :width="7"
      ></v-progress-circular>
    </v-overlay>
    <div>
        <v-row class="ma-0 mb-3">
          <h2>{{ title }}</h2>
          <router-link
                    class="no-text-decoration"
                    :to="`/admin/registration/pedigrees/new`"                    
                  >                  
                  <v-btn
                    class="ml-2"
                    color="primary"
                    >+ New Pedigree Order</v-btn>
                </router-link>
          <v-spacer></v-spacer>
          <v-btn color="primary" v-on:click="loadPedigreeOrders">Apply Filters</v-btn
          >&nbsp;
          <v-btn color="secondary" v-on:click="clearAllFilters">Clear</v-btn>
        </v-row>
    </div>
    <PaginationSelect
      :totalCount="paginatedResponse.totalCount"
      :page="options.page"
      :size="options.itemsPerPage"
      v-on:load="loadPedigreeOrders"
      v-on:changePage="options.page = $event"
      v-on:changeSize="options.itemsPerPage = $event"
    />
    <v-row>
      <v-alert width="100%" class="mr-5 ml-5" v-if="!!errorMessage" dismissible type="error"> {{ errorMessage }}</v-alert>
    </v-row>     
    <v-dialog v-model="showReportDialog">
      <report-viewer
          :reportName="Report_Name"
          :dataUrl="Report_URL"
          ref="reportViewer"
          :printOnly="true"
      />   
    </v-dialog>  
    <v-data-table 
        dense
        fixed-header
        class="elevation-1 mt-2 mb-2 pt-2"
        :loading="loading"
        :headers="headers" 
        :options.sync="options"
        :items="paginatedResponse.data"
        :server-items-length="paginatedResponse.totalCount"              
        hide-default-footer
        item-key="id"
        v-model="selectedPedigreeOrders"
        :show-select="isDogReportTypePedigreeSelected"
        >
          <template v-slot:header.data-table-select="{ header }">
              <v-checkbox label="All"
                v-model="allSelected"
                :disabled="false"
                class="ma-0 pa-0"></v-checkbox>
          </template>
          <template v-slot:header.actionButton="{ header }">                  
            <v-btn color="primary" :disabled="selectedPedigreeOrders.length == 0 || !isDogReportTypePedigreeSelected" v-on:click="printSelectedPedigrees(searchParams.printType)">Print Selected Pedigrees</v-btn>
            <v-btn color="primary"  :disabled="selectedPedigreeOrders.length == 0 || printTypes.find(pt =>pt.id === searchParams.printType).printable === false" v-on:click="printSelectedLabels">Print Labels</v-btn>
            <v-btn color="secondary"  :disabled="selectedPedigreeOrders.length == 0 || printTypes.find(pt =>pt.id === searchParams.printType).printable === false" v-on:click="markSelectedPrinted">Mark Printed</v-btn>
          </template>
          <template v-slot:header.dogId="{ header }">
            <v-text-field hide-details
              v-model="searchParams.dogId"
              class="mt-2 pt-2 mb-2"
              label="Dog ID#"
              clearable
              @click:clear="searchParams.dogId = ''"
              v-on:keyup.enter="loadPedigreeOrders"
            />
        </template>
        <template v-slot:header.dogName="{ header }">
            <v-text-field hide-details
              class="mt-2 pt-2 mb-2"
              v-model="searchParams.dogName"
              label="Dog Name"
              clearable
              @click:clear="searchParams.dogName = ''"
              v-on:keyup.enter="loadPedigreeOrders"
              v-on:click:clear="loadPedigreeOrders"
            />
        </template>
        <template v-slot:header.personName="{ header }">
            <v-text-field hide-details
              class="mt-2 pt-2 mb-2"
              v-model="searchParams.personName"
              label="Person Name"
              clearable
              @click:clear="searchParams.dogName = ''"
              v-on:keyup.enter="loadPedigreeOrders"
              v-on:click:clear="loadPedigreeOrders"
            />
        </template>
        <template v-slot:header.printType="{ header }">
          <v-select
            v-model="searchParams.printType"
            label="Print Type"
            :items="printTypes"
            item-text="name"
            item-value="id"
            width="80px"
          ></v-select
          ><br />
        </template>
        <template v-slot:header.createdAt="{ header }">
          <div width="300px">
            <datepicker 
              :date="searchParams.createdDateFrom" 
              label="Ordered Date - From" 
              v-on:updateDates="updateDates($event, 'createdDateFrom')"
              v-on:enter="updateDateSearch($event, 'createdDateFrom')"
            />
            <datepicker
              :date="searchParams.createdDateTo"
              label="Ordered Date - To"
              v-on:updateDates="updateDates($event, 'createdDateTo')"
              v-on:enter="updateDateSearch($event, 'createdDateTo')"
            />
          </div>
      </template>
        <template v-slot:header.printedAt="{ header }">
          <div width="300px">
            <v-checkbox
              :indeterminate="searchParams.printed == null"
              v-model="searchParams.printed"
              label="Printed"
              v-on:change="toggleCheckbox"
            />
            <datepicker 
              :date="searchParams.printedDateFrom" 
              label="Printed From" 
              v-on:updateDates="updateDates($event, 'printedDateFrom')"
              v-on:enter="updateDateSearch($event, 'printedDateFrom')"
            />
            <datepicker
              :date="searchParams.printedDateTo"
              label="Printed To"
              v-on:updateDates="updateDates($event, 'printedDateTo')"
              v-on:enter="updateDateSearch($event, 'printedDateTo')"
            />
          </div>
      </template>      
      <template v-slot:item.personName="{ item }">
        <router-link :to="`/admin/people/${item.personId}`" target="_blank">
          {{ item.personName }}
        </router-link>
      </template>
      <template v-slot:item.printedAt="{ item }">
        <localized-date v-if="!!item.printedAt" :date="instantToLocalDate(item.printedAt)" />
      </template>            
      <template v-slot:item.createdAt="{ item }">
        <localized-date :date="instantToLocalDate(item.createdAt)" />
      </template>       
      <template v-slot:item.accessStartsAt="{ item }">
        <span v-if="!!item.accessStartsAt">From: <localized-date :date="instantToLocalDate(item.accessStartsAt)" /></span><br/>
        <span v-if="!!item.accessEndsAt">To: <localized-date :date="instantToLocalDate(item.accessEndsAt)" /></span>
      </template>      
      <template v-slot:item.printType="{ item }">             
            {{ (printTypes.find(i => i.id === item.printType))?.name ?? item.printType}}
        </template> 
        <template v-slot:item.actionButton="{ item }">             
            <v-btn color="primary" v-on:click="showReport([item.dogId], item.printType)">Show Report</v-btn>
            <v-btn color="primary"  :disabled="printTypes.find(pt =>pt.id === item.printType).printable === false" v-on:click="showPrintLabelDialog([item.personId])">Label</v-btn>
            <v-btn color="secondary" :disabled="printTypes.find(pt =>pt.id === item.printType).printable === false"  v-on:click="markPrinted([item.id])">Mark Printed</v-btn>
        </template>  
    </v-data-table>
  </v-sheet>
</template>

<script lang="ts">
import Vue from "vue";
import pedigreeService from "@/services/admin/pedigreeService";
import { getErrorMessage } from "@/services/errorHandler";
import { UserInfo } from "@/types/User";
import ReportViewer from "@/components/Admin/Reports/ReportViewer.vue";
import { PaginationOptions, PaginatedResponse } from "@/types/Pagination";
import Datepicker from "@/components/common/inputs/DatepickerFormatted.vue";
import PaginationSelect from "@/components/common/PaginationSelect.vue";
import { Messages, PedigreeTypes } from "@/constants";
import { instantToLocalDate } from "@/helpers/date-utils";

import lookupsService, { getPedigreePrintTypesList } from "@/services/lookupsService";

    export default Vue.extend({
      name: "PedigreeOrdersList",
      components: {
        Datepicker,
        PaginationSelect,
        ReportViewer
      },
      data: function() {
        return {
          selectedPedigreeOrders: [] as any[],
          allSelected: false,
          title: 'Pedigree Orders',
          loading: false,
          isError: false,
          errorMessage: "",
          Report_Name: '',
          Report_URL: '',
          showReportDialog: false,
          paginatedResponse: {} as PaginatedResponse<object>,
          searchParams: {
            dogId: "",
            dogName: "", 
            personName: "", 
            printType: 1,
            createdDateFrom: null as Temporal.PlainDate | null,
            createdDateTo: null as Temporal.PlainDate | null,
            printedDateFrom: null as Temporal.PlainDate | null,
            printedDateTo: null as Temporal.PlainDate | null,
            printed: false as boolean | null,
          },    
          options: { itemsPerPage: 100, page: 1 } as PaginationOptions,   
          headers: [
            { text: "Dog ID#", align: "left", value: "dogId", width: "75px",  sortable: false },
            { text: "Dog Name", align: "left", value: "dogName", width: "150px",  sortable: false },
            { text: "Person Name", align: "left", value: "personName", width: "100px",  sortable: false },
            { text: "Print Type", align: "left", value: "printType", width: "50px",  sortable: false },
            { text: "Ordered At", align: "left", value: "createdAt", width: "100px",  sortable: false },
            { text: "Printed At", align: "left", value: "printedAt", width: "100px",  sortable: false },
            { text: "Online access period", align: "left", value: "accessStartsAt", width: "100px",  sortable: false },
            { text: "", align: "left", value: "actionButton", width: "100px", sortable: false },
            { text: "Submitted By", align: "right", value: "submittedByAdminName", width: "50px", sortable: false, visible: false },
            { text: "Notes", align: "right", value: "adminNotes", width: "50px", sortable: false },
          ],
          printTypes: [] as any[],          
          currentPrintedCheckboxIdx: 0,
        };
      },
      created: function () {
        this.printTypes = getPedigreePrintTypesList();
        if(this.userInfo){
          this.loadPedigreeOrders();
        }
      },
      computed: {
        userInfo(): UserInfo {
          return this.$store.state.userInfo;
        },
        isDogReportTypePedigreeSelected(): boolean {
          return !!this.$data.searchParams && lookupsService.isDogReportTypePedigree(this.$data.searchParams.printType);
        },
      },
      watch: {      
        userInfo: function (newVal: object) { 
          this.loadPedigreeOrders();
        },
        allSelected: function (newValue) {
            if(newValue){
                this.selectedPedigreeOrders = this.paginatedResponse.data;
            } else {
                this.selectedPedigreeOrders = [];
                
            }
        },
      },
      methods: {
        instantToLocalDate,
        toggleCheckbox: function () {
          var states = [false, true, null, ];
          var next = ++this.currentPrintedCheckboxIdx % states.length;
          this.currentPrintedCheckboxIdx = next;
          this.searchParams.printed = states[next];
        },
        loadPedigreeOrders: async function()  { 
          this.loading = true; 
          try {
            const response = await pedigreeService.getPedigreeOrdersList(
              this.options,
              this.searchParams
            );
    
              this.loading = false;
              this.paginatedResponse = response;
            } catch (ex: any) {    
              this.loading = false;

              this.errorMessage = getErrorMessage(ex);
          }
        },
        showReport: function (dogIds: string[], reportType: number){
          if(lookupsService.isDogReportTypePedigree(reportType)){
            this.Report_Name = "Asca.Api.Reports.rpt3240_Dog_Pedigree_Report_New2.rdlx";
            this.Report_URL = `/reports/pedigrees?dogIds=${dogIds.map(dogId => encodeURIComponent(dogId.trim())).join('&dogIds=')}`;
          } else if (lookupsService.isDogReportTypeProgeny(reportType)) {
            this.Report_Name = "Asca.Api.Reports.rpt3250_Dog_Progeny_Report.rdlx";
            this.Report_URL = `/reports/progeny/${encodeURIComponent(dogIds[0].trim())}`;
          }
          this.showReportDialog = true;
        },        
        showPrintLabelDialog: function (personIds: number[]) {
            this.Report_Name = "Asca.Api.Reports.rpt1210_Person_Label.rdlx";
            this.Report_URL = `/reports/contacts?personIds=${personIds.join('&personIds=')}`;
            this.showReportDialog = true;
        },
        updateDates: function (val: any, key: string) {
          (this as any).searchParams[key] = val.date;
        },
        updateDateSearch: async function(val: any, key: string) {
          await this.updateDates(val, key);
          this.loadPedigreeOrders();
        },
        clearAllFilters: function () {
          this.options.page = 1;
          this.options.itemsPerPage = 100;
          this.searchParams = {
            dogId: "",
            dogName: "",  
            personName: "", 
            printType: 1,
            createdDateFrom: null,
            createdDateTo: null,
            printedDateFrom: null,
            printedDateTo: null,
            printed: null
          };
        },
        addNewPedigreeOrder: function() {

        },        
        printSelectedPedigrees(printType: number){
          var pedigreesDogsIds = this.selectedPedigreeOrders.map(order => order.dogId);
          this.showReport(pedigreesDogsIds, printType);
        },       
        printSelectedLabels(){
          var pedigreesPersonIds = this.selectedPedigreeOrders.map(order => order.personId);
          this.showPrintLabelDialog(pedigreesPersonIds);
        },
        markSelectedPrinted: function(){
          var pedigreesOrdersIds = this.selectedPedigreeOrders.map(order => order.id);
          this.markPrinted(pedigreesOrdersIds);
        },
        markPrinted: function(ids: number[]){
          this.errorMessage = "";
          this.loading = true; 
          pedigreeService.markPedigreeOrdersPrinted(ids)
          .then((res) => {
              if (res.success) {
                this.loadPedigreeOrders();
              } else {
                this.errorMessage = Messages.SaveError + " " + res.messages.join(", ");
              }
          })
          .catch((e) => {
            this.errorMessage = Messages.SaveError;
          })
          .finally(() => this.loading = false);

        }
      }
    });
    </script>
